/**
 * Braintree Client Service
 * 
 * Wrapper for Braintree Drop-in UI initialization and management.
 * Implements Braintree Drop-in UI 1.x integration per plan.md tech stack.
 * 
 * Task: T052
 * 
 * Features:
 * - Drop-in UI instance management
 * - Client token validation
 * - Nonce extraction and validation
 * - Error handling for Braintree failures
 * - Cleanup and teardown
 */

import dropin from 'braintree-web-drop-in';

/**
 * Create Braintree Drop-in UI instance
 * 
 * @param {Object} options - Drop-in configuration
 * @param {string} options.authorization - Braintree client token
 * @param {string} options.container - DOM selector for Drop-in container
 * @param {Object} [options.paymentOptionPriority] - Payment method priority
 * @param {Object} [options.card] - Card-specific options
 * @returns {Promise<Object>} Drop-in instance
 * @throws {Error} If Drop-in creation fails
 */
export async function createDropinInstance(options) {
  try {
    const dropinInstance = await dropin.create({
      authorization: options.authorization,
      container: options.container,
      // Payment method configuration
      paymentOptionPriority: options.paymentOptionPriority || ['card'],
      // Card options
      card: options.card || {
        cardholderName: {
          required: true, // Require cardholder name
        },
        cvv: {
          required: true,
        },
        expirationDate: {
          required: true,
        },
        overrides: {
          fields: {
            number: {
              supportedCardBrands: {
                visa: true,
                mastercard: true,
                'american-express': true,
                discover: true,
                jcb: false,
                'union-pay': false,
              },
            },
          },
        },
      },
      // Localization
      locale: 'en_US',
      // Styling
      styles: options.styles || {},
    });
    
    console.log('[Braintree] Drop-in UI created successfully');
    
    return dropinInstance;
  } catch (error) {
    console.error('[Braintree] Failed to create Drop-in UI:', error);
    throw new Error('Failed to load payment form. Please refresh the page and try again.');
  }
}

/**
 * Request payment method nonce from Drop-in UI
 * 
 * @param {Object} dropinInstance - Braintree Drop-in instance
 * @returns {Promise<Object>} Payment method payload
 * @returns {string} nonce - Payment method nonce
 * @returns {string} type - Payment method type (e.g., 'CreditCard')
 * @returns {Object} details - Additional payment method details
 * @throws {Error} If nonce request fails
 */
export async function requestPaymentMethod(dropinInstance) {
  if (!dropinInstance) {
    throw new Error('Drop-in instance not initialized');
  }
  
  try {
    const payload = await dropinInstance.requestPaymentMethod();
    
    console.log('[Braintree] Payment method received:', {
      type: payload.type,
      nonce: payload.nonce ? 'present' : 'missing',
    });
    
    if (!payload.nonce) {
      throw new Error('No payment method nonce received');
    }
    
    return payload;
  } catch (error) {
    console.error('[Braintree] Failed to get payment method:', error);
    
    // Handle specific Braintree errors
    if (error.message && error.message.includes('No payment method is available')) {
      throw new Error('Please enter valid payment information');
    }
    
    throw new Error('Failed to process payment information. Please try again.');
  }
}

/**
 * Check if Drop-in UI is already initialized
 * 
 * @param {Object|null} dropinInstance - Braintree Drop-in instance
 * @returns {boolean} Whether Drop-in is initialized
 */
export function isDropinInitialized(dropinInstance) {
  return dropinInstance !== null && dropinInstance !== undefined;
}

/**
 * Teardown Drop-in UI instance and clean up resources
 * 
 * @param {Object} dropinInstance - Braintree Drop-in instance
 * @returns {Promise<void>}
 */
export async function teardownDropin(dropinInstance) {
  if (!dropinInstance) {
    return;
  }
  
  try {
    await dropinInstance.teardown();
    console.log('[Braintree] Drop-in UI torn down successfully');
  } catch (error) {
    console.error('[Braintree] Failed to teardown Drop-in UI:', error);
    // Non-critical error - don't throw
  }
}

/**
 * Clear any selected payment method in Drop-in UI
 * 
 * @param {Object} dropinInstance - Braintree Drop-in instance
 * @returns {Promise<void>}
 */
export async function clearSelectedPaymentMethod(dropinInstance) {
  if (!dropinInstance) {
    return;
  }
  
  try {
    await dropinInstance.clearSelectedPaymentMethod();
    console.log('[Braintree] Payment method cleared');
  } catch (error) {
    console.error('[Braintree] Failed to clear payment method:', error);
  }
}

/**
 * Check if Drop-in UI has a payment method available
 * 
 * @param {Object} dropinInstance - Braintree Drop-in instance
 * @returns {boolean} Whether a payment method is available
 */
export function isPaymentMethodRequestable(dropinInstance) {
  if (!dropinInstance) {
    return false;
  }
  
  return dropinInstance.isPaymentMethodRequestable();
}

/**
 * Validate client token format
 * 
 * @param {string} clientToken - Braintree client token
 * @returns {boolean} Whether token is valid format
 */
export function isValidClientToken(clientToken) {
  // Braintree client tokens are base64-encoded strings
  if (!clientToken || typeof clientToken !== 'string') {
    return false;
  }
  
  // Basic validation: should be a long base64 string
  const base64Regex = /^[A-Za-z0-9+/]+=*$/;
  return base64Regex.test(clientToken) && clientToken.length > 100;
}

/**
 * Handle Braintree Drop-in UI events
 * 
 * @param {Object} dropinInstance - Braintree Drop-in instance
 * @param {Object} handlers - Event handlers
 * @param {Function} [handlers.onPaymentMethodRequestable] - Called when payment method becomes requestable
 * @param {Function} [handlers.onNoPaymentMethodRequestable] - Called when no payment method is requestable
 * @param {Function} [handlers.onPaymentOptionSelected] - Called when payment option is selected
 */
export function setupDropinEventHandlers(dropinInstance, handlers = {}) {
  if (!dropinInstance) {
    return;
  }
  
  if (handlers.onPaymentMethodRequestable) {
    dropinInstance.on('paymentMethodRequestable', (event) => {
      console.log('[Braintree] Payment method requestable:', event);
      handlers.onPaymentMethodRequestable(event);
    });
  }
  
  if (handlers.onNoPaymentMethodRequestable) {
    dropinInstance.on('noPaymentMethodRequestable', () => {
      console.log('[Braintree] No payment method requestable');
      handlers.onNoPaymentMethodRequestable();
    });
  }
  
  if (handlers.onPaymentOptionSelected) {
    dropinInstance.on('paymentOptionSelected', (event) => {
      console.log('[Braintree] Payment option selected:', event.paymentOption);
      handlers.onPaymentOptionSelected(event);
    });
  }
}

export default {
  createDropinInstance,
  requestPaymentMethod,
  isDropinInitialized,
  teardownDropin,
  clearSelectedPaymentMethod,
  isPaymentMethodRequestable,
  isValidClientToken,
  setupDropinEventHandlers,
};
